import { useState, useEffect } from 'react';
import { GlassCard, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Users,
  CheckSquare,
  Clock,
  Download,
  Award,
  BarChart3,
  FileText,
  Calendar,
  X,
  Star,
  Briefcase
} from 'lucide-react';
import {
  Staff,
  DailyReport,
  staffService,
  taskService,
  attendanceService,
  dailyReportService
} from '@/lib/storage';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface StaffStats {
  staff: Staff;
  totalTasks: number;
  completedTasks: number;
  completionRate: number;
  avgWorkingHours: number;
  attendanceRate: number;
  productivityScore: number;
}

const AdminReportsPage = () => {
  const [staffStats, setStaffStats] = useState<StaffStats[]>([]);
  const [dailyReports, setDailyReports] = useState<DailyReport[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [selectedStaffDate, setSelectedStaffDate] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [overallStats, setOverallStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    avgCompletionRate: 0,
    totalWorkingHours: 0,
  });

  useEffect(() => {
    calculateData();
    loadReports();
    const interval = setInterval(() => {
      calculateData();
      loadReports();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const calculateData = async () => {
    try {
      const [staff, tasks, attendance] = await Promise.all([
        staffService.getAll(),
        taskService.getAll(),
        attendanceService.getAll()
      ]);

      setStaffList(staff);

      const stats: StaffStats[] = staff.map((s) => {
        const staffTasks = tasks.filter(t => t.assignedStaff?.includes(s.id) || t.assignedTo === s.id);
        const completedTasks = staffTasks.filter(t => t.status === 'Completed').length;
        const staffAttendance = attendance.filter(a => a.staffId === s.id);

        const avgWorkingHours = staffAttendance.length > 0
          ? staffAttendance.reduce((sum, a) => sum + (a.workingHours || 0), 0) / staffAttendance.length
          : 0;

        const uniqueDays = new Set(staffAttendance.map(a => a.date)).size;
        const totalPossibleDays = 30;
        const attendanceRate = Math.min(100, (uniqueDays / totalPossibleDays) * 100);
        const completionRate = staffTasks.length > 0 ? (completedTasks / staffTasks.length) * 100 : 0;

        const productivityScore = Math.round(
          (completionRate * 0.5) + (attendanceRate * 0.3) + (Math.min(avgWorkingHours / 8, 1) * 20)
        );

        return {
          staff: s,
          totalTasks: staffTasks.length,
          completedTasks,
          completionRate: Math.round(completionRate),
          avgWorkingHours: Math.round(avgWorkingHours * 10) / 10,
          attendanceRate: Math.round(attendanceRate),
          productivityScore: Math.min(100, productivityScore),
        };
      });

      setStaffStats(stats.sort((a, b) => b.productivityScore - a.productivityScore));

      setOverallStats({
        totalTasks: tasks.length,
        completedTasks: tasks.filter(t => t.status === 'Completed').length,
        avgCompletionRate: stats.length > 0
          ? Math.round(stats.reduce((sum, s) => sum + s.completionRate, 0) / stats.length)
          : 0,
        totalWorkingHours: Math.round(attendance.reduce((sum, a) => sum + (a.workingHours || 0), 0)),
      });
    } catch (error) {
      console.error("Failed to calculate stats", error);
    }
  };

  const loadReports = async () => {
    try {
      const reports = await dailyReportService.getAll();
      setDailyReports(reports.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } catch (error) {
      console.error("Failed to load reports", error);
    }
  };

  const exportReport = () => {
    const headers = ['Staff Name', 'Department', 'Total Tasks', 'Completed', 'Completion Rate', 'Avg Hours', 'Productivity Score'];
    const rows = staffStats.map(s => [
      s.staff.name,
      s.staff.department,
      s.totalTasks,
      s.completedTasks,
      `${s.completionRate}%`,
      `${s.avgWorkingHours}h`,
      s.productivityScore,
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `productivity-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const filteredReports = dailyReports.filter(r => {
    const matchesStaff = selectedStaffDate === 'all' || r.staffId === selectedStaffDate;
    const matchesDate = !selectedDate || r.date === selectedDate;
    return matchesStaff && matchesDate;
  });

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 60) return 'text-blue-600 dark:text-blue-400';
    if (score >= 40) return 'text-amber-600 dark:text-amber-400';
    return 'text-rose-600 dark:text-rose-455';
  };

  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-gradient-to-r from-emerald-500 to-teal-500';
    if (score >= 60) return 'bg-gradient-to-r from-blue-500 to-indigo-500';
    if (score >= 40) return 'bg-gradient-to-r from-amber-500 to-yellow-500';
    return 'bg-gradient-to-r from-rose-500 to-red-500';
  };

  // Animation Grid Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 350, damping: 25 } }
  };

  return (
    <div className="space-y-6">
      {/* Header Panel */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Reports & Analytics</h2>
          <p className="text-sm text-slate-500">Track task completion ratios and performance analytics.</p>
        </div>
        <Button
          variant="outline"
          onClick={exportReport}
          className="rounded-xl font-semibold flex items-center justify-center gap-2 border-slate-200 dark:border-slate-800 w-full sm:w-auto h-11"
        >
          <Download className="w-4.5 h-4.5" /> Export Report
        </Button>
      </div>

      <Tabs defaultValue="productivity" className="space-y-6">
        <TabsList className="p-1 bg-slate-200/50 dark:bg-slate-900/50 backdrop-blur-md rounded-xl border border-slate-200/30 dark:border-slate-800/30 w-full sm:w-auto max-w-sm flex gap-1">
          <TabsTrigger value="productivity" className="flex-1 rounded-lg py-2.5 font-semibold text-sm flex items-center justify-center gap-2 transition-all">
            <BarChart3 className="w-4.5 h-4.5" /> Productivity Stats
          </TabsTrigger>
          <TabsTrigger value="daily-reports" className="flex-1 rounded-lg py-2.5 font-semibold text-sm flex items-center justify-center gap-2 transition-all">
            <FileText className="w-4.5 h-4.5" /> Daily Reports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="productivity" className="space-y-6 outline-none">
          {/* Productivity Stats Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {[
              { label: 'Total Tasks Assigned', value: overallStats.totalTasks, color: 'text-slate-900 dark:text-white', icon: CheckSquare, bg: 'bg-blue-500/10 dark:bg-blue-500/20', textIcon: 'text-blue-600 dark:text-blue-400' },
              { label: 'Completed Tasks', value: overallStats.completedTasks, color: 'text-emerald-650 dark:text-emerald-400', icon: TrendingUp, bg: 'bg-emerald-500/10 dark:bg-emerald-500/20', textIcon: 'text-emerald-650 dark:text-emerald-400' },
              { label: 'Completion rate', value: `${overallStats.avgCompletionRate}%`, color: 'text-indigo-650 dark:text-indigo-400', icon: BarChart3, bg: 'bg-indigo-500/10 dark:bg-indigo-500/20', textIcon: 'text-indigo-600 dark:text-indigo-400' },
              { label: 'Total hours worked', value: `${overallStats.totalWorkingHours}h`, color: 'text-amber-600 dark:text-amber-400', icon: Clock, bg: 'bg-amber-500/10 dark:bg-amber-500/20', textIcon: 'text-amber-600 dark:text-amber-400' },
            ].map(s => (
              <motion.div key={s.label} variants={itemVariants}>
                <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
                  <CardContent className="p-5 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{s.label}</p>
                      <p className={`text-2xl font-black mt-2 leading-none ${s.color}`}>{s.value}</p>
                    </div>
                    <div className={`w-11 h-11 rounded-2xl ${s.bg} flex items-center justify-center ${s.textIcon}`}>
                      <s.icon className="w-5.5 h-5.5" />
                    </div>
                  </CardContent>
                </GlassCard>
              </motion.div>
            ))}
          </motion.div>

          {/* Performance Ranking */}
          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate="show"
          >
            <GlassCard className="border-slate-200/50 dark:border-slate-850 shadow-sm rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl overflow-hidden">
              <CardHeader className="pb-3 pt-6 px-6 border-b border-slate-100 dark:border-slate-800/40">
                <CardTitle className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-500" />
                  Staff Productivity Index Ranking
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {staffStats.length === 0 ? (
                  <div className="text-center py-12 text-slate-400 font-medium">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-40 animate-pulse" />
                    <p>No staff records discovered. Register team members first.</p>
                  </div>
                ) : (
                  <div className="space-y-3.5">
                    {staffStats.map((stat, index) => (
                      <motion.div
                        key={stat.staff.id}
                        whileHover={{ scale: 1.008 }}
                        className="flex items-center gap-4 p-4 rounded-2xl border border-slate-150 dark:border-slate-850/80 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all duration-200"
                      >
                        {/* Rank Badge */}
                        <div className={`w-8.5 h-8.5 rounded-xl flex items-center justify-center text-xs font-black shrink-0 ${
                          index === 0 ? 'bg-amber-500/15 text-amber-600 border border-amber-500/25' :
                          index === 1 ? 'bg-slate-300/20 text-slate-600 dark:text-slate-300' :
                          index === 2 ? 'bg-amber-700/10 text-amber-800 dark:text-amber-500' :
                          'bg-slate-100/50 dark:bg-slate-800/30 text-slate-550'
                        }`}>
                          {index === 0 ? <Star className="w-4 h-4 fill-current" /> : index + 1}
                        </div>

                        {/* Avatar Details */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-indigo-500/5 dark:bg-indigo-500/10 border border-indigo-550/10 flex items-center justify-center shrink-0 text-indigo-600 dark:text-indigo-400">
                            <span className="font-extrabold text-sm">
                              {stat.staff.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-slate-850 dark:text-slate-100 truncate text-sm">{stat.staff.name}</p>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide flex items-center gap-1.5 mt-0.5">
                              <Briefcase className="w-3.5 h-3.5" />
                              {stat.staff.department}
                            </span>
                          </div>
                        </div>

                        {/* Metrics Details */}
                        <div className="hidden sm:flex items-center gap-8 text-xs font-semibold text-slate-500">
                          <div className="text-center">
                            <p className="font-bold text-slate-800 dark:text-slate-200">{stat.completedTasks}/{stat.totalTasks}</p>
                            <p className="text-[9px] text-slate-400 uppercase tracking-wide mt-0.5">Tasks</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-slate-800 dark:text-slate-200">{stat.completionRate}%</p>
                            <p className="text-[9px] text-slate-400 uppercase tracking-wide mt-0.5">Completion</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-slate-800 dark:text-slate-200">{stat.avgWorkingHours}h</p>
                            <p className="text-[9px] text-slate-400 uppercase tracking-wide mt-0.5">Avg hours</p>
                          </div>
                        </div>

                        {/* Productivity Ring/Score */}
                        <div className="flex items-center gap-4.5">
                          <div className="hidden md:block w-24 bg-slate-100 dark:bg-slate-850 h-2 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${getScoreBg(stat.productivityScore)} transition-all duration-500`}
                              style={{ width: `${stat.productivityScore}%` }}
                            />
                          </div>
                          <div className="text-right shrink-0">
                            <span className={`text-base font-extrabold ${getScoreColor(stat.productivityScore)}`}>
                              {stat.productivityScore}
                            </span>
                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wide">Score</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </GlassCard>
          </motion.div>
        </TabsContent>

        <TabsContent value="daily-reports" className="space-y-6 outline-none">
          {/* Daily Reports Filters */}
          <div className="flex flex-col md:flex-row items-center gap-4 mb-2">
            <Select value={selectedStaffDate} onValueChange={setSelectedStaffDate}>
              <SelectTrigger className="h-11 w-full md:w-[220px] rounded-xl border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-950/40">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-400" />
                  <SelectValue placeholder="Filter by Staff" />
                </div>
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">All Members</SelectItem>
                {staffList.map((staff) => (
                  <SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2 w-full md:w-auto">
              <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 dark:border-slate-800 px-3 bg-white/50 dark:bg-slate-950/40 w-full sm:max-w-[220px] h-11">
                <Calendar className="w-4.5 h-4.5 text-slate-400 shrink-0" />
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="border-none bg-transparent focus-visible:ring-0 p-0 text-sm font-medium"
                />
              </div>
              {selectedDate && (
                <Button variant="ghost" size="icon" onClick={() => setSelectedDate('')} className="rounded-xl shrink-0">
                  <X className="w-4.5 h-4.5 text-slate-400" />
                </Button>
              )}
            </div>

            <div className="md:ml-auto text-xs font-bold text-slate-400 uppercase tracking-wide">
              {filteredReports.length} Submission{filteredReports.length !== 1 && 's'} logged
            </div>
          </div>

          {/* Daily Reports Submissions Grid */}
          <AnimatePresence mode="wait">
            {filteredReports.length === 0 ? (
              <motion.div
                key="empty-reports"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-16 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl bg-white/30 dark:bg-slate-900/10"
              >
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-40 animate-pulse" />
                <p className="text-sm text-slate-400 font-semibold">No daily submissions reported for this filter.</p>
              </motion.div>
            ) : (
              <motion.div
                key="grid-reports"
                variants={containerVariants}
                initial="hidden"
                animate="show"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {filteredReports.map((report) => (
                  <motion.div key={report.id} variants={itemVariants} whileHover={{ scale: 1.015 }}>
                    <GlassCard className="h-full border-slate-200/50 dark:border-slate-850 shadow-sm rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl flex flex-col justify-between overflow-hidden">
                      <CardContent className="p-6 flex flex-col justify-between h-full">
                        <div>
                          <div className="flex items-start justify-between gap-3 mb-4">
                            <div className="flex items-center gap-3.5">
                              <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 text-primary">
                                <span className="font-extrabold text-sm">
                                  {report.staffName.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <p className="font-bold text-sm text-slate-850 dark:text-slate-100 truncate">{report.staffName}</p>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide flex items-center gap-1 mt-0.5">
                                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                  {new Date(report.date).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                            </div>
                            <Badge variant="outline" className="text-[9px] font-bold px-1.5 h-4.5 rounded-md border-slate-200 dark:border-slate-800">
                              {new Date(report.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Badge>
                          </div>

                          <div className="bg-slate-100/50 dark:bg-slate-950/40 border border-slate-100 dark:border-slate-850 rounded-2xl p-4 text-xs font-semibold leading-relaxed text-slate-700 dark:text-slate-350 min-h-[120px] whitespace-pre-wrap">
                            {report.content}
                          </div>
                        </div>
                      </CardContent>
                    </GlassCard>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminReportsPage;
